import React, { useState, useRef, useEffect } from 'react';
import './CardHand.css';
import CardDetailModal from './CardDetailModal';
import { seedBadgeText, seedProgressLabel } from '../utils/seedTier';

// ── Card type icons and colors ──
const CARD_TYPE_STYLE = {
  attack:  { icon: '⚔️', color: '#e74c3c', label: 'Attack' },
  spell:   { icon: '🔮', color: '#9b59b6', label: 'Spell' },
  block:   { icon: '🛡️', color: '#3498db', label: 'Block' },
  skill:   { icon: '🌳', color: '#27ae60', label: 'Skill' },
  power:   { icon: '⚡', color: '#f39c12', label: 'Power' },
  special: { icon: '✨', color: '#1abc9c', label: 'Special' },
  heal:    { icon: '💚', color: '#10b981', label: 'Heal' },
  utility: { icon: '🔧', color: '#9ca3af', label: 'Utility' },
};

// ── Card source labels ──
const SOURCE_LABELS = {
  class:          'Class',
  equipment:      'Equipment',
  seed:           'Seed',
  skill_tree:     'Skill Tree',
  advanced_class: 'Adv. Class',
  race:           'Race',
  alignment:      'Alignment',
  level_up:       'Level Up',
  set_bonus:      'Set Bonus',
  junk:           'Junk',
};

// ── Element styling ──
const ELEMENT_STYLE = {
  Fire:        { icon: '🔥', color: '#e74c3c' },
  Cold:        { icon: '❄️', color: '#3498db' },
  Ice:         { icon: '❄️', color: '#3498db' },   // Bug #23: alias used by Mage cards
  Electricity: { icon: '⚡', color: '#f1c40f' },
  Lightning:   { icon: '⚡', color: '#f1c40f' },   // Bug #23: alias used by Mage cards
  Acid:        { icon: '🧪', color: '#27ae60' },
  Water:       { icon: '💧', color: '#2980b9' },
  Earth:       { icon: '🪨', color: '#8B7355' },
  Air:         { icon: '🌪️', color: '#bdc3c7' },
  Sonic:       { icon: '🔊', color: '#9b59b6' },
  Dark:        { icon: '🌑', color: '#6c3483' },
  Light:       { icon: '☀️', color: '#f9e79f' },
  Holy:        { icon: '✝️', color: '#f9e79f' },    // Bug #23: used by Cleric/alignment cards
  Psionic:     { icon: '🧠', color: '#a569bd' },
  Arcane:      { icon: '💫', color: '#8e44ad' },
  Healing:     { icon: '💚', color: '#27ae60' },
};

// ── Keyword badge colors ──
const KEYWORD_STYLE = {
  Exhaust:    { color: '#e74c3c', bg: 'rgba(231,76,60,0.2)' },
  Retain:     { color: '#f39c12', bg: 'rgba(243,156,18,0.2)' },
  Innate:     { color: '#2ecc71', bg: 'rgba(46,204,113,0.2)' },
  Ethereal:   { color: '#9b59b6', bg: 'rgba(155,89,182,0.2)' },
  Unplayable: { color: '#e74c3c', bg: 'rgba(231,76,60,0.15)' },
};

// ── Rarity glow colors ──
const RARITY_STYLE = {
  common:    { border: '#555', glow: 'none' },
  uncommon:  { border: '#2ecc71', glow: '0 0 6px rgba(46,204,113,0.4)' },
  rare:      { border: '#3498db', glow: '0 0 8px rgba(52,152,219,0.5)' },
  epic:      { border: '#9b59b6', glow: '0 0 10px rgba(155,89,182,0.6)' },
  legendary: { border: '#f39c12', glow: '0 0 14px rgba(243,156,18,0.7), 0 0 28px rgba(243,156,18,0.3)' },
  junk:      { border: '#7f8c8d', glow: 'none' },
};

// ============================================================
// SINGLE CARD COMPONENT
// ============================================================

// Quick-play gesture tuning
const CARD_CLICK_DELAY_MS = 200;   // wait to see if a double-click follows before enlarging
const CARD_SWIPE_PLAY_PX = 55;     // upward drag distance that plays the card
const CARD_SWIPE_MAX_DX = 45;      // more horizontal travel than this is a scroll, not a play

const Card = ({ card, canPlay, isSelected, isPending, onSelect, onPlay, onInspect }) => {
  const typeStyle = CARD_TYPE_STYLE[card.type] || CARD_TYPE_STYLE.special;
  const rarityStyle = RARITY_STYLE[card.rarity] || RARITY_STYLE.common;
  const element = card._spellMeta?.element || card.effects?.magicDamage?.element;
  const elemStyle = element ? ELEMENT_STYLE[element] : null;
  const isJunk = card.source === 'junk' || card.isJunk || card.rarity === 'junk';
  const isSetBonus = card.source === 'set_bonus' || card.effects?._setBonus;

  // Quick-play shortcuts: double-click (desktop) and swipe/drag-up both play instantly, so
  // experienced players skip the enlarge step. Single click still enlarges.
  const [dragUp, setDragUp] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dragRect, setDragRect] = useState(null);
  const cardRef = useRef(null);
  const gesture = useRef(null);
  const clickTimer = useRef(null);
  const suppressClick = useRef(false);

  useEffect(() => () => { if (clickTimer.current) clearTimeout(clickTimer.current); }, []);

  const quickPlay = () => {
    // Play if able; otherwise enlarge so the player can see why it can't be played.
    if (canPlay && onPlay) onPlay(card.id);
    else onSelect(card.id);
  };

  const handleClick = () => {
    if (suppressClick.current) { suppressClick.current = false; return; }
    if (clickTimer.current) clearTimeout(clickTimer.current);
    // Brief delay so a double-click can cancel the enlarge and play instead.
    clickTimer.current = setTimeout(() => { clickTimer.current = null; onSelect(card.id); }, CARD_CLICK_DELAY_MS);
  };

  const handleDoubleClick = () => {
    if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; }
    quickPlay();
  };

  const handlePointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    gesture.current = { x: e.clientX, y: e.clientY, mode: null, id: e.pointerId };
  };

  const handlePointerMove = (e) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y; // negative = upward
    if (g.mode === null) {
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) { g.mode = 'scroll'; return; }
      if (dy < -8 && Math.abs(dy) >= Math.abs(dx)) {
        g.mode = 'drag';
        // Freeze the card's current spot, then lift it out of the clipping scroll row so it
        // floats over the monster instead of sliding under the HUD.
        const el = cardRef.current;
        if (el) {
          const r = el.getBoundingClientRect();
          setDragRect({ left: r.left, top: r.top, width: r.width, height: r.height });
          try { el.setPointerCapture(g.id); } catch (_) { /* capture optional */ }
        }
        setDragging(true);
      } else { return; }
    }
    if (g.mode === 'drag') setDragUp(Math.min(220, Math.max(0, -dy)));
  };

  const handlePointerUp = (e) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (g.mode === 'drag') {
      setDragging(false);
      setDragRect(null);
      setDragUp(0);
      if (-dy >= CARD_SWIPE_PLAY_PX && Math.abs(dx) <= CARD_SWIPE_MAX_DX) { suppressClick.current = true; quickPlay(); }
      else suppressClick.current = true; // a deliberate drag shouldn't also enlarge
    } else if (g.mode === 'scroll') {
      suppressClick.current = true;      // a horizontal scroll-drag shouldn't enlarge
    }
  };

  const handlePointerCancel = () => { gesture.current = null; setDragging(false); setDragRect(null); setDragUp(0); };

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (onInspect) onInspect(card);
  };

  // Seed progress (shows distance to the next upgrade tier, not a saturating star count)
  const seedLevel = card._seedLevel || 0;
  const showSeedBadge = card.source === 'seed' && seedLevel > 0;

  const cardClass = `card-hand-card ${card.type} ${canPlay ? 'playable' : 'unplayable'} ${isSelected ? 'selected' : ''} ${isPending ? 'card-pending' : ''} ${dragging ? 'dragging' : ''} ${dragUp >= CARD_SWIPE_PLAY_PX ? 'will-play' : ''} rarity-${card.rarity} ${isJunk ? 'junk' : ''} ${isSetBonus ? 'set-bonus' : ''}`;

  const cardStyle = (dragging && dragRect)
    ? {
        // Fixed positioning escapes the hand's overflow clip so the card floats over the monster.
        position: 'fixed',
        left: dragRect.left,
        top: dragRect.top,
        width: dragRect.width,
        margin: 0,
        transform: `translateY(-${dragUp}px)`,
        transition: 'none',
        zIndex: 10000,
        borderColor: isJunk ? '#7f8c8d' : rarityStyle.border,
        boxShadow: '0 14px 34px rgba(0,0,0,0.65)',
      }
    : {
        borderColor: isPending ? '#f1c40f' : (isSelected ? '#f1c40f' : (isJunk ? '#7f8c8d' : rarityStyle.border)),
        boxShadow: isPending ? '0 0 18px rgba(241,196,15,0.9)' : (isSelected ? '0 0 15px rgba(241,196,15,0.7)' : rarityStyle.glow),
      };

  return (
    <>
      {dragging && dragRect && (
        <div
          key="ph"
          className="card-drag-placeholder"
          style={{ width: dragRect.width, height: dragRect.height, flexShrink: 0 }}
          aria-hidden="true"
        />
      )}
      <div
        key="card"
        ref={cardRef}
        className={cardClass}
        style={cardStyle}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onContextMenu={handleContextMenu}
        title={card.name}
      >
      {/* v643.1: in-flight spinner overlay while server processes the play */}
      {isPending && (
        <div className="card-pending-spinner" aria-hidden="true">⟳</div>
      )}

      {/* Legendary sparkle overlay */}
      {card.rarity === 'legendary' && <div className="card-legendary-sparkle" />}

      {/* AP cost badge */}
      <div className="card-ap-cost" style={{ background: isJunk ? '#6b7280' : (canPlay ? typeStyle.color : '#555') }}>
        {card.apCost}
      </div>

      {/* Set bonus badge */}
      {isSetBonus && (
        <div className="card-set-badge" title={`Set bonus${card._setPieces ? ` (${card._setPieces}pc)` : ''}`}>
          {card._setPieces ? `${card._setPieces}pc` : 'SET'}
        </div>
      )}

      {/* Seed upgrade progress */}
      {showSeedBadge && (
        <div className="card-seed-stars" title={seedProgressLabel(seedLevel)}>
          {seedBadgeText(seedLevel)}
        </div>
      )}

      {/* Card header — full name always shown (wraps to 2 lines), so players learn card names */}
      <div className="card-header">
        <span className="card-type-icon">{typeStyle.icon}</span>
        <span className="card-name" title={card.name}>{card.name}</span>
      </div>

      {/* Element badge */}
      {elemStyle && (
        <div className="card-element" style={{ color: elemStyle.color }}>
          {elemStyle.icon} {element}
        </div>
      )}

      {/* MP cost for spell cards */}
      {card._spellMeta?.mp && (
        <div className="card-mp-cost">
          {card._spellMeta.mp} MP
        </div>
      )}

      {/* Card description */}
      <div className="card-description">
        {card.description}
      </div>

      {/* Keywords */}
      {card.keywords && card.keywords.length > 0 && (
        <div className="card-keywords">
          {card.keywords.map((kw, i) => {
            const kwStyle = KEYWORD_STYLE[kw] || { color: '#aaa', bg: 'rgba(150,150,150,0.2)' };
            return (
              <span key={i} className="card-keyword" style={{ color: kwStyle.color, background: kwStyle.bg }}>
                {kw}
              </span>
            );
          })}
        </div>
      )}

      {/* Source badge */}
      <div className="card-source">
        {SOURCE_LABELS[card.source] || card.source}
      </div>
      </div>
    </>
  );
};

// ============================================================
// CARD HAND COMPONENT (the full hand + AP/Block display)
// ============================================================

const CardHand = ({ deckState, character, characterMp, onPlayCard, onEndTurn, onScryResolve, onExhaustResolve, onDeckExhaustResolve, isPlayerTurn, pendingCardId, onTrack, redrawMode, redrawSelectedIds, onRedrawToggle, onRedrawConfirm, onRedrawCancel, modernLayout }) => {
  const track = onTrack || (() => {}); // no-op fallback
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [scrySelections, setScrySelections] = useState({}); // cardId → 'top' | 'bottom'
  const [exhaustSelections, setExhaustSelections] = useState({});
  const [deckExhaustSelections, setDeckExhaustSelections] = useState({});
  const [inspectedCard, setInspectedCard] = useState(null);
  const handRef = useRef(null);

  // v480 fix: clear stale selections when pending choice changes (bug #43)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { setExhaustSelections({}); }, [deckState?.pendingExhaustChoice]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { setDeckExhaustSelections({}); }, [deckState?.pendingDeckExhaustChoice]);

  if (!deckState) return null;

  const {
    hand,
    drawPileCount,
    discardPile,
    exhaustPile,
    ap,
    maxAp,
    block,
    canAct,
    scryPending,
    pendingExhaustChoice,
    pendingDeckExhaustChoice,
    pendingDeckExhaustCards,
  } = deckState;

  const handleSelect = (cardId) => {
    const card = hand.find(c => c.id === cardId);
    if (cardId === selectedCardId) {
      track('card_deselect', card?.name || cardId);
      setSelectedCardId(null);
    } else {
      track('card_select', `${card?.name || '?'} (id:${cardId} cost:${card?.apCost ?? '?'}AP)`);
      setSelectedCardId(cardId);
    }
  };

  const handlePlay = (cardId) => {
    if (!isPlayerTurn) return;
    // v643.1: another card is already in-flight — block stacking plays
    if (pendingCardId && pendingCardId !== cardId) return;
    const card = hand.find(c => c.id === cardId);
    // v480 fix: also block Unplayable keyword cards (server validates but saves a round-trip)
    if (!card || ap < card.apCost || (card.keywords && card.keywords.includes('Unplayable'))) {
      track('card_play_blocked', `${card?.name || '?'} (need:${card?.apCost ?? '?'}AP have:${ap}AP)`);
      return;
    }
    // Check MP affordability for spell cards
    const mpCost = card.effects?.mpCost || card._spellMeta?.mp || 0;
    if (mpCost > 0 && characterMp != null && characterMp < mpCost) {
      track('card_play_blocked', `${card.name} (need:${mpCost}MP have:${characterMp}MP)`);
      return;
    }
    onPlayCard(cardId);
    // Don't clear selectedCardId here — the server response will remove the
    // card from hand, which auto-hides the play bar.  If the action is
    // blocked (actionSentRef), the card stays selected so the player can retry.
  };

  const handleEndTurn = () => {
    setSelectedCardId(null);
    onEndTurn();
  };

  // ── Scry resolution helpers ──
  const handleScryToggle = (cardId) => {
    setScrySelections(prev => {
      const current = prev[cardId];
      // v480 fix: start at 'bottom' so first click has visible effect (default = 'top')
      if (!current) return { ...prev, [cardId]: 'bottom' };
      if (current === 'bottom') return { ...prev, [cardId]: 'top' };
      // Remove the key (cycle back to unset = default 'top')
      const { [cardId]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleScryConfirm = () => {
    if (!scryPending || !onScryResolve) return;
    const topIds = [];
    const bottomIds = [];
    for (const card of scryPending) {
      const choice = scrySelections[card.id] || 'top'; // default keep on top
      if (choice === 'top') topIds.push(card.id);
      else bottomIds.push(card.id);
    }
    setScrySelections({});
    onScryResolve(topIds, bottomIds);
  };

  const handleExhaustToggle = (cardId) => {
    const max = pendingExhaustChoice?.max || 1;
    setExhaustSelections(prev => {
      if (prev[cardId]) {
        const { [cardId]: removed, ...rest } = prev;
        return rest;
      }
      if (Object.keys(prev).length >= max) return prev;
      return { ...prev, [cardId]: true };
    });
  };

  const handleExhaustConfirm = () => {
    if (!pendingExhaustChoice || !onExhaustResolve) return;
    onExhaustResolve(Object.keys(exhaustSelections));
    setExhaustSelections({});
  };

  const handleDeckExhaustToggle = (cardId) => {
    const max = pendingDeckExhaustChoice?.max || 1;
    setDeckExhaustSelections(prev => {
      if (prev[cardId]) {
        const { [cardId]: removed, ...rest } = prev;
        return rest;
      }
      if (Object.keys(prev).length >= max) return prev;
      return { ...prev, [cardId]: true };
    });
  };

  const handleDeckExhaustConfirm = () => {
    if (!pendingDeckExhaustChoice || !onDeckExhaustResolve) return;
    onDeckExhaustResolve(Object.keys(deckExhaustSelections));
    setDeckExhaustSelections({});
  };

  const deckExhaustCards = pendingDeckExhaustCards || [];

  // AP pip display
  const apPips = [];
  for (let i = 0; i < maxAp; i++) {
    apPips.push(
      <span key={i} className={`ap-pip ${i < ap ? 'filled' : 'empty'}`}>
        ◆
      </span>
    );
  }
  // Bonus AP beyond maxAp
  if (ap > maxAp) {
    for (let i = 0; i < ap - maxAp; i++) {
      apPips.push(
        <span key={`bonus-${i}`} className="ap-pip bonus">
          ◆
        </span>
      );
    }
  }

  return (
    <div className={`card-hand-container${modernLayout ? ' modern-layout' : ''}`}>
      {/* Status bar: AP, Block, Deck info */}
      <div className="card-hand-status">
        <div className="status-section ap-section">
          <span className="status-label">AP</span>
          <div className="ap-pips">{apPips}</div>
          <span className="status-value">{ap}/{maxAp}</span>
        </div>

        {block > 0 && (
          <div className="status-section block-section">
            <span className="status-label">🛡️ Block</span>
            <span className="status-value block-value">{block}</span>
          </div>
        )}

        <div className="status-section deck-info">
          <span className="deck-count" title={`Draw pile: ${drawPileCount} remaining out of ${drawPileCount + hand.length + discardPile.length + exhaustPile.length} total`}>
            📚 {drawPileCount}/{drawPileCount + hand.length + discardPile.length + exhaustPile.length}
          </span>
          <span className="discard-count" title="Discard pile">
            ♻️ {discardPile.length}
          </span>
          {exhaustPile.length > 0 && (
            <span className="exhaust-count" title="Exhausted (removed)">
              🚫 {exhaustPile.length}
            </span>
          )}
        </div>

        <button
          className={`end-turn-btn ${isPlayerTurn && !canAct ? 'no-plays' : ''}`}
          onClick={handleEndTurn}
          disabled={!isPlayerTurn}
          title="End your turn [E / Space]"
        >
          End Turn ⏩
        </button>
      </div>

      {/* Card hand */}
      <div className="card-hand-cards" ref={handRef}>
        {hand.length === 0 ? (
          <div className="hand-empty">No cards in hand.</div>
        ) : (
          hand.map((card) => {
            // v700: redraw mode (Phial of Foresight) overrides normal play affordances —
            // every card is selectable regardless of AP/MP cost; click toggles selection.
            if (redrawMode) {
              const isPicked = redrawSelectedIds && redrawSelectedIds.has(card.id);
              return (
                <Card
                  key={card.id}
                  card={card}
                  canPlay={true}
                  isSelected={isPicked}
                  isPending={false}
                  onSelect={onRedrawToggle}
                  onInspect={setInspectedCard}
                />
              );
            }
            const affordable = ap >= card.apCost;
            const mpCost = card.effects?.mpCost || card._spellMeta?.mp || 0;
            const mpAffordable = mpCost <= 0 || characterMp == null || characterMp >= mpCost;
            const playable = isPlayerTurn && affordable && mpAffordable && !card.keywords?.includes('Unplayable');
            return (
              <Card
                key={card.id}
                card={card}
                canPlay={playable && !pendingCardId}
                isSelected={selectedCardId === card.id}
                isPending={pendingCardId === card.id}
                onSelect={handleSelect}
                onPlay={handlePlay}
                onInspect={setInspectedCard}
              />
            );
          })
        )}
      </div>

      {/* v700: Redraw mode banner (Phial of Foresight) */}
      {redrawMode && (
        <div className="scry-overlay" style={{ borderColor: '#0070dd' }}>
          <div className="scry-header">
            <span className="scry-icon">🔮</span>
            <h4>Phial of Foresight</h4>
            <p className="scry-hint">
              Click cards to mark them for discard. Confirm to discard {redrawSelectedIds ? redrawSelectedIds.size : 0} card{(redrawSelectedIds && redrawSelectedIds.size === 1) ? '' : 's'} and draw the same number.
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 8 }}>
            <button
              className="scry-confirm-btn"
              onClick={onRedrawConfirm}
              disabled={!redrawSelectedIds || redrawSelectedIds.size === 0}
              style={(!redrawSelectedIds || redrawSelectedIds.size === 0) ? { opacity: 0.5 } : undefined}
            >
              Confirm ({redrawSelectedIds ? redrawSelectedIds.size : 0})
            </button>
            <button
              className="scry-confirm-btn"
              onClick={onRedrawCancel}
              style={{ background: '#555' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Scry overlay — shown when scryPending has cards */}
      {scryPending && scryPending.length > 0 && (
        <div className="scry-overlay">
          <div className="scry-header">
            <span className="scry-icon">🔮</span>
            <h4>Scrying — Top of Deck</h4>
            <p className="scry-hint">Click cards to toggle: <strong>Keep on Top</strong> or <strong>Send to Bottom</strong></p>
          </div>
          <div className="scry-cards">
            {scryPending.map((card) => {
              const choice = scrySelections[card.id] || 'top';
              const typeStyle = CARD_TYPE_STYLE[card.type] || CARD_TYPE_STYLE.special;
              return (
                <div
                  key={card.id}
                  className={`scry-card scry-${choice}`}
                  onClick={() => handleScryToggle(card.id)}
                  style={{ borderTopColor: typeStyle.color }}
                >
                  <div className="scry-card-ap">{card.apCost}</div>
                  <div className="scry-card-name">{typeStyle.icon} {card.name}</div>
                  <div className="scry-card-desc">{card.description}</div>
                  <div className={`scry-choice-badge ${choice}`}>
                    {choice === 'top' ? '⬆️ Keep on Top' : '⬇️ Send to Bottom'}
                  </div>
                </div>
              );
            })}
          </div>
          <button className="scry-confirm-btn" onClick={handleScryConfirm}>
            Confirm Scry
          </button>
        </div>
      )}

      {pendingExhaustChoice && hand.length > 0 && (
        <div className="scry-overlay">
          <div className="scry-header">
            <span className="scry-icon">🗑️</span>
            <h4>Choose Cards to Exhaust</h4>
            <p className="scry-hint">Select up to <strong>{pendingExhaustChoice.max || 1}</strong> card(s) from your hand.</p>
          </div>
          <div className="scry-cards">
            {hand.map((card) => {
              const selected = !!exhaustSelections[card.id];
              const typeStyle = CARD_TYPE_STYLE[card.type] || CARD_TYPE_STYLE.special;
              return (
                <div
                  key={card.id}
                  className={`scry-card ${selected ? 'scry-bottom' : 'scry-top'}`}
                  onClick={() => handleExhaustToggle(card.id)}
                  style={{ borderTopColor: typeStyle.color }}
                >
                  <div className="scry-card-ap">{card.apCost}</div>
                  <div className="scry-card-name">{typeStyle.icon} {card.name}</div>
                  <div className="scry-card-desc">{card.description}</div>
                  <div className={`scry-choice-badge ${selected ? 'bottom' : 'top'}`}>
                    {selected ? '🗑️ Exhaust' : 'Keep'}
                  </div>
                </div>
              );
            })}
          </div>
          <button className="scry-confirm-btn" onClick={handleExhaustConfirm} disabled={Object.keys(exhaustSelections).length < (pendingExhaustChoice.min || 1)}>
            Confirm Exhaust
          </button>
        </div>
      )}

      {pendingDeckExhaustChoice && deckExhaustCards.length > 0 && (
        <div className="scry-overlay">
          <div className="scry-header">
            <span className="scry-icon">🗂️</span>
            <h4>Choose Deck Cards to Exhaust</h4>
            <p className="scry-hint">Select up to <strong>{pendingDeckExhaustChoice.max || 1}</strong> card(s) from your visible deck piles.</p>
          </div>
          <div className="scry-cards">
            {deckExhaustCards.map((card) => {
              const selected = !!deckExhaustSelections[card.id];
              const typeStyle = CARD_TYPE_STYLE[card.type] || CARD_TYPE_STYLE.special;
              return (
                <div
                  key={card.id}
                  className={`scry-card ${selected ? 'scry-bottom' : 'scry-top'}`}
                  onClick={() => handleDeckExhaustToggle(card.id)}
                  style={{ borderTopColor: typeStyle.color }}
                >
                  <div className="scry-card-ap">{card.apCost}</div>
                  <div className="scry-card-name">{typeStyle.icon} {card.name}</div>
                  <div className="scry-card-desc">{card.description}</div>
                  <div className={`scry-choice-badge ${selected ? 'bottom' : 'top'}`}>
                    {selected ? '🗑️ Exhaust' : 'Keep'}
                  </div>
                </div>
              );
            })}
          </div>
          <button className="scry-confirm-btn" onClick={handleDeckExhaustConfirm} disabled={Object.keys(deckExhaustSelections).length < (pendingDeckExhaustChoice.min || 1)}>
            Confirm Deck Exhaust
          </button>
        </div>
      )}

      {/* Selected card — enlarged detail view; tap the card again to play, tap outside to cancel */}
      {selectedCardId && !redrawMode && (() => {
        const card = hand.find(c => c.id === selectedCardId);
        if (!card) return null;
        const affordable = ap >= card.apCost;
        const mpCost = card.effects?.mpCost || card._spellMeta?.mp || 0;
        const mpAffordable = mpCost <= 0 || characterMp == null || characterMp >= mpCost;
        const isUnplayableKw = !!card.keywords?.includes('Unplayable');
        const canPlayCard = isPlayerTurn && affordable && mpAffordable && !isUnplayableKw && !pendingCardId;
        const reason = !isPlayerTurn ? 'Not your turn'
          : !affordable ? 'Not enough AP'
          : !mpAffordable ? 'Not enough MP'
          : isUnplayableKw ? 'Cannot be played directly'
          : null;
        return (
          <CardDetailModal
            card={card}
            onClose={() => setSelectedCardId(null)}
            onPlay={() => handlePlay(card.id)}
            canPlay={canPlayCard}
            playDisabledReason={reason}
            isPending={pendingCardId === card.id}
          />
        );
      })()}

      {/* Card detail inspection modal (right-click / read-only) */}
      {inspectedCard && (
        <CardDetailModal card={inspectedCard} onClose={() => setInspectedCard(null)} />
      )}
    </div>
  );
};

export default CardHand;
